import gql from 'graphql-tag';
import { ApolloError, useQuery } from '@apollo/client';
import { useFlags } from 'launchdarkly-react-client-sdk';

interface Props {
  planId?: string;
}

interface Return {
  data?: { isDualBilling: boolean; isUtilityConsolidatedBilling: boolean };
  error?: ApolloError;
  loading: boolean;
}

export const useBillingType = ({ planId }: Props): Return => {
  const flags = useFlags();
  let result;
  const { data, loading, error } = useQuery<
    GetPlanTemplateIdQuery,
    GetPlanTemplateIdQueryVariables
  >(GET_PLAN_TEMPLATE_ID, {
    skip: !planId,
    variables: { planId: Number(planId) || -1 },
  });
  const { utilityAccounts } = data?.plan || {};

  if (utilityAccounts) {
    // Feature flag: cs-acb-retirement
    // When enabled, use targetBillExperience to determine billing type
    const csAcbRetirement = flags.csAcbRetirement;

    const isUtilityBilled = utilityAccounts.some(
      ({
        targetBillExperience,
        activeBillingSettings,
        funnelBillExperience,
      }) => {
        const billExperience = csAcbRetirement
          ? (targetBillExperience as string) ??
            activeBillingSettings?.billExperience ??
            funnelBillExperience
          : activeBillingSettings?.billExperience ?? funnelBillExperience;
        return billExperience === 'utility_billed';
      }
    );
    const isDualBilled = utilityAccounts.some(
      ({
        targetBillExperience,
        activeBillingSettings,
        funnelBillExperience,
      }) => {
        const billExperience = csAcbRetirement
          ? (targetBillExperience as string) ??
            activeBillingSettings?.billExperience ??
            funnelBillExperience
          : activeBillingSettings?.billExperience ?? funnelBillExperience;
        return billExperience === 'dual_billed';
      }
    );
    result = {
      isDualBilling: isDualBilled,
      isUtilityConsolidatedBilling: !isDualBilled && isUtilityBilled,
    };
  }
  return { data: result, error, loading };
};

export const GET_PLAN_TEMPLATE_ID = gql`
  query GetPlanTemplateId($planId: Int!) {
    plan(id: $planId) {
      id
      planTemplateId
      utilityAccounts {
        activeBillingSettings {
          billExperience
        }
        targetBillExperience
        funnelBillExperience
      }
    }
  }
`;

export const getPlanTemplateIdACBMock = {
  plan: {
    id: 1,
    planTemplateId: 5,
    utilityAccounts: [
      {
        targetBillExperience: 'arcadia_billed',
        funnelBillExperience: 'arcadia_billed',
        activeBillingSettings: { billExperience: 'arcadia_billed' },
      },
    ],
  },
};

export const getPlanTemplateIdDBMock = {
  plan: {
    id: 1,
    planTemplateId: 343,
    utilityAccounts: [
      {
        targetBillExperience: 'dual_billed',
        funnelBillExperience: 'dual_billed',
        activeBillingSettings: { billExperience: 'dual_billed' },
      },
    ],
  },
};

export const getPlanTemplateIdUCBMock = {
  plan: {
    id: 1,
    planTemplateId: 211,
    utilityAccounts: [
      {
        targetBillExperience: 'utility_billed',
        funnelBillExperience: 'utility_billed',
        activeBillingSettings: { billExperience: 'utility_billed' },
      },
    ],
  },
};

export const useBillingTypeMock = {
  data: { isDualBilling: false, isUtilityConsolidatedBilling: false },
  loading: false,
};
